import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Grid, Typography, styled } from '@mui/material';
import { Checkbox, DatePicker, InputOnlyField, Text } from '@ngt/forms';
import { useScopedField } from '@ngt/forms-core';
import { FC } from 'react';
import { ErrorFields } from '../errors/ErrorFields';
import { StyledAccordianSummary, StyledAccordion, StyledAccordionDetails } from './SpeakerForm';
import * as Dtos from '@/api/dtos';

const columnMapping = {
    xs: 12,
    sm: 6,
    md: 3
}

const StyledTypography = styled(Typography)(({theme}) => ({
    paddingBottom: theme.spacing(2),
}))

interface SpeakerAccommodationSectionProps {
}

const AccommodationSectionUpdate: FC<SpeakerAccommodationSectionProps> = ({
}) => {

    const {
        state: {
            value: postalCountry
        }
    } = useScopedField<string>('postalCountry', {value: true});

	const {
		state: { value: registrationAffiliation },
	} = useScopedField<number>("registrationAffiliation", { value: true });

	const {
		state: { value: country },
	} = useScopedField<string>("country", { value: true });

	// Computed text fragments based on affiliation and location
	const isDomesticDelegate =
		postalCountry?.toLowerCase() === "australia" ||
		postalCountry?.toLowerCase() === "new zealand" ||
		postalCountry?.toLowerCase() === "aus" ||
		postalCountry?.toLowerCase() === "nz" ||
		country?.toLowerCase() === "australia" ||
		country?.toLowerCase() === "new zealand" ||
		country?.toLowerCase() === "aus" ||
		country?.toLowerCase() === "nz";

	const isInternationalSpeaker = !isDomesticDelegate && registrationAffiliation === Dtos.RegistrationAffiliation.GuestSpeaker;
	const isDomesticSpeaker = isDomesticDelegate && registrationAffiliation === Dtos.RegistrationAffiliation.GuestSpeaker;
	const isTraineeGrant = registrationAffiliation === Dtos.RegistrationAffiliation.TraineeAndEarlyCareersDayGrant;
	const isEducationGrant = registrationAffiliation === Dtos.RegistrationAffiliation.EducationTravelGrant;

	const showAdditionalAccommodationText = !isInternationalSpeaker;
	const showGuestInfo = isInternationalSpeaker;
	const showSpeakerDates = isDomesticSpeaker;

	const {
		state: { value: accommodationArrangementsNotRequired },
	} = useScopedField("accommodationArrangementsNotRequired", { value: true });

    return (
        <>
            <StyledAccordion elevation={4}>
                <StyledAccordianSummary 
                    expandIcon={<FontAwesomeIcon fixedWidth icon={faChevronDown} color='white' />}
                >
                    <Typography variant="h3" color="white"><b>Accommodation</b></Typography>   
                </StyledAccordianSummary>
                <StyledAccordionDetails>

                {isTraineeGrant && (
                    <StyledTypography variant='body1'>
                        The T&EC Grant includes accommodation for Friday 31st July (depending on departure city BCT will support accommodation Saturday 1st August).
                    </StyledTypography>
                )}

                <StyledTypography variant='body1'>
                    Accommodation (breakfast included) will be booked at the <a href='https://www.idem.events/r/anz-breast-cancer-trials'>Sofitel</a> Brisbane Central, in Queensland{isTraineeGrant || isEducationGrant ? "." : ", for your stay during the conference."}
                </StyledTypography>

                {showAdditionalAccommodationText && (
                    <StyledTypography variant='body1'>
                        Additional accommodation outside of your conference registration can be booked and will be invoiced to you at the contracted Sofitel rate with BCT. Payment is required prior to attending the ASM.
                    </StyledTypography>
                )}

                {showSpeakerDates && (
                    <>
                        <StyledTypography variant='body1'>
                            For speakers presenting in the main ASM, accommodation is for a check in on Tuesday 28 July or Wednesday 29 July, and check out on Friday 31 July or Saturday 1st August.
                        </StyledTypography>
                        <StyledTypography variant='body1'>
                            For speakers presenting at the Trainee and Early Career Day only, accommodation supported by BCT is for a check in on Friday 29th July and check out on Sunday 2nd August.
                        </StyledTypography>
                    </>
                )}

                {showGuestInfo && (
                    <StyledTypography variant='body1'>
                        Should you be attending the conference with a guest, BCT will arrange for appropriate bedding set ups as requested. Guests of international speakers are also welcome to attend all social functions.
                    </StyledTypography>
                )}

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Grid container columnSpacing={1} mt={4}>
                            <Grid item xs={12} md={8} mt={1} mb={4}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography>
                                        I am a local Brisbane resident and I <strong>do not require</strong> <strong>accommodation arrangements</strong> for the ASM.
                                    </Typography>
                                    <InputOnlyField
                                        propertyPath="accommodationArrangementsNotRequired"
                                        component={Checkbox}
                                        FormControlProps={{
                                            fullWidth: false
                                        }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            spacing={4}
                        >
                            <Grid  
                                item
                                {...columnMapping}
                            >
                                <InputOnlyField
                                    label="Check In Date"
                                    propertyPath="checkInDate"
                                    component={DatePicker}
                                    slotProps={{
                                        textField: {
                                            variant: "standard",
                                            margin: "normal"
                                        }
                                    }}
                                    disabled={accommodationArrangementsNotRequired}
                                />
                            </Grid>
                            <Grid  
                                item
                                {...columnMapping}
                            >
                                <InputOnlyField
                                    label="Check Out Date"
                                    propertyPath="checkOutDate"
                                    component={DatePicker}
                                    slotProps={{
                                        textField: {
                                            variant: "standard",
                                            margin: "normal"
                                        }
                                    }}
                                    disabled={accommodationArrangementsNotRequired}
                                />
                            </Grid>
                            <Grid  
                                item
                                {...columnMapping}
                            >
                                <InputOnlyField
                                    label="Special Requests, i.e. cot, quiet room"
                                    propertyPath="specialRequests"
                                    component={Text}
                                    variant="standard"
                                    margin="normal"
                                    disabled={accommodationArrangementsNotRequired}
                                />
                            </Grid>

                        </Grid>
                    </Box>
                </StyledAccordionDetails>
            </StyledAccordion>
            <ErrorFields
                errorFields={[
                    "checkInDate",
                    "checkOutDate",
                    "specialRequests"
                ]}
            />
        </>

    )
}

export default AccommodationSectionUpdate;
